// RemindersTab — Automated Reminders view (legacy reports.php tab).
//
// Stats strip → date-nav card → breakdown chip row → table.
// Date input is constrained to dates that actually carry reminder
// activity (server returns `available_dates`). Prev/Next walk that
// list, skipping inactive days; Today snaps to today's date.

import { useMemo, useState } from 'react';
import {
  Bell,
  BellOff,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Layers,
  Mail,
  MessageSquare,
  Smartphone,
  XCircle,
} from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card/Card';
import { EmptyState } from '../../../../components/ui/EmptyState/EmptyState';
import { StatusBadge } from '../../../../components/ui/StatusBadge/StatusBadge';
import { StatsStrip } from '../../../../components/ui/StatsStrip/StatsStrip';
import type { BadgeVariant } from '../../../../components/ui/Badge/Badge';
import {
  useRemindersReport,
  type ReminderRow,
} from '../../hooks/useRemindersReport';
import styles from './RemindersTab.module.css';

const BOOKING_STATUS_MAP: Record<string, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
  cancellation_requested: 'danger',
};

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatHumanDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m === null) return iso;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatShortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m === null) return iso;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string | null): string {
  if (iso === null) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function timeframeClass(tf: string): string {
  const upper = tf.toUpperCase();
  if (upper.includes('/') || upper.includes('-')) return styles.tfAmpm ?? '';
  if (upper.startsWith('AM')) return styles.tfAm ?? '';
  return styles.tfPm ?? '';
}

export function RemindersTab() {
  const [date, setDate] = useState<string>(() => todayIsoDate());
  const query = useRemindersReport(date);
  const data = query.data;

  const availableDates = useMemo(
    () => data?.available_dates ?? [],
    [data?.available_dates],
  );

  function navigateToAvailable(direction: 'prev' | 'next') {
    if (availableDates.length === 0) return;
    // available_dates is DESC. Find the closest available date relative
    // to the current `date` and step in the requested direction.
    if (direction === 'prev') {
      // Earlier than current.
      const earlier = availableDates.find((d) => d < date);
      if (earlier !== undefined) setDate(earlier);
    } else {
      // Later than current.
      const reversed = [...availableDates].reverse();
      const later = reversed.find((d) => d > date);
      if (later !== undefined) setDate(later);
    }
  }

  function handleToday() {
    setDate(todayIsoDate());
  }

  const minDate = availableDates.length > 0 ? availableDates[availableDates.length - 1] : undefined;
  const maxDate = availableDates.length > 0 ? availableDates[0] : undefined;
  const hasPrev = availableDates.some((d) => d < date);
  const hasNext = availableDates.some((d) => d > date);

  const totals = data?.totals;
  const breakdown = data?.breakdown;
  const rows: ReminderRow[] = data?.rows ?? [];
  const isToday = date === todayIsoDate();

  return (
    <div className={styles.tab}>
      {/* ── Summary tiles ───────────────────────────────────────────────── */}
      <StatsStrip
        ariaLabel="Reminder counters"
        tiles={[
          { key: 'today', label: 'Reminders Today', value: rows.length },
          { key: 'email', label: 'All-Time Email', value: totals?.all_time_email ?? '—' },
          { key: 'sms', label: 'All-Time SMS', value: totals?.all_time_sms ?? '—' },
          { key: 'queue-pending', label: 'Queue Pending', value: totals?.queue_pending ?? '—' },
          { key: 'queue-sent', label: 'Queue Sent', value: totals?.queue_sent ?? '—' },
          { key: 'queue-failed', label: 'Queue Failed', value: totals?.queue_failed ?? '—' },
        ]}
      />

      {/* ── Date navigation ─────────────────────────────────────────────── */}
      <div className={styles.dateNav}>
        <Bell size={16} aria-hidden style={{ color: 'var(--color-secondary)' }} />
        <div className={styles.dateLabel}>
          {formatHumanDate(date)}
          {isToday ? <span className={styles.todayPill}>Today</span> : null}
          <small>
            {availableDates.length} day
            {availableDates.length !== 1 ? 's' : ''} with reminders on record
          </small>
        </div>
        <input
          type="date"
          aria-label="Reminder date"
          className={styles.dateInput}
          value={date}
          onChange={(e) => {
            if (e.target.value) setDate(e.target.value);
          }}
          min={minDate}
          max={maxDate}
        />
        <div className={styles.dateNavSep} />
        <Button
          variant="ghost"
          size="sm"
          icon={ChevronLeft}
          aria-label="Previous reminder day"
          onClick={() => navigateToAvailable('prev')}
          disabled={!hasPrev}
        >
          Prev
        </Button>
        <Button
          variant="primary"
          size="sm"
          icon={CalendarDays}
          onClick={handleToday}
        >
          Today
        </Button>
        <Button
          variant="ghost"
          size="sm"
          icon={ChevronRight}
          aria-label="Next reminder day"
          onClick={() => navigateToAvailable('next')}
          disabled={!hasNext}
        >
          Next
        </Button>
      </div>

      {/* ── Breakdown chips ─────────────────────────────────────────────── */}
      {breakdown ? (
        <div className={styles.breakdownBar}>
          <Badge variant="info" icon={<Mail size={12} aria-hidden />}>
            {breakdown.email_only} Email Only
          </Badge>
          <Badge variant="success" icon={<Smartphone size={12} aria-hidden />}>
            {breakdown.sms_only} SMS Only
          </Badge>
          <Badge variant="default" icon={<Layers size={12} aria-hidden />}>
            {breakdown.both} Both
          </Badge>
          <Badge variant="success" icon={<CheckCircle2 size={12} aria-hidden />}>
            {breakdown.status_completed} Completed
          </Badge>
          <Badge variant="warning" icon={<Clock size={12} aria-hidden />}>
            {breakdown.status_pending} Pending
          </Badge>
          <Badge variant="danger" icon={<XCircle size={12} aria-hidden />}>
            {breakdown.status_cancelled} Cancelled
          </Badge>
        </div>
      ) : null}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <Card
        flush
        title={`Automated Reminders — ${formatShortDate(date)}`}
        actions={
          <span className={styles.cardMeta}>
            {rows.length} reminder{rows.length !== 1 ? 's' : ''} sent
          </span>
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            icon={BellOff}
            title="No reminders sent on this date"
            description="Try selecting a different date, or check that automated reminders are enabled."
          />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Booking Date</th>
                  <th>Driver</th>
                  <th>Email Reminder</th>
                  <th>SMS Reminder</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.booking_ref}-${r.email_reminder_sent_at ?? r.sms_reminder_sent_at ?? ''}`}>
                    <td>
                      <span className={styles.cellRef}>{r.booking_ref}</span>
                    </td>
                    <td>
                      <div className={styles.cellPrimary}>{r.customer_name}</div>
                      {r.customer_phone ? (
                        <div className={styles.cellSub}>{r.customer_phone}</div>
                      ) : null}
                      {r.customer_email ? (
                        <div className={styles.cellSub}>{r.customer_email}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className={styles.cellPrimary}>{r.car_make_model}</div>
                      {r.car_registration ? (
                        <div style={{ marginTop: '0.25rem' }}>
                          <span className={styles.regChip}>{r.car_registration}</span>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div>{formatShortDate(r.booking_date)}</div>
                      {r.timeframe ? (
                        <div className={`${styles.tfBadge} ${timeframeClass(r.timeframe)}`}>
                          {r.timeframe}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {r.driver_name ? (
                        <span className={styles.driverChip}>{r.driver_name}</span>
                      ) : (
                        <span className={styles.unassigned}>Unassigned</span>
                      )}
                    </td>
                    <td>
                      {r.email_reminder_sent ? (
                        <span className={`${styles.methodChip} ${styles.methodEmail}`}>
                          <Mail size={12} aria-hidden /> Sent
                          {r.email_reminder_sent_at ? (
                            <span className={styles.methodTime}>
                              {' · '}
                              {formatTime(r.email_reminder_sent_at)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className={`${styles.methodChip} ${styles.methodNone}`}>
                          Not sent
                        </span>
                      )}
                    </td>
                    <td>
                      {r.sms_reminder_sent ? (
                        <span className={`${styles.methodChip} ${styles.methodSms}`}>
                          <MessageSquare size={12} aria-hidden /> Sent
                          {r.sms_reminder_sent_at ? (
                            <span className={styles.methodTime}>
                              {' · '}
                              {formatTime(r.sms_reminder_sent_at)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className={`${styles.methodChip} ${styles.methodNone}`}>
                          Not sent
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusBadge
                        status={r.booking_status || null}
                        statusMap={BOOKING_STATUS_MAP}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
